import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'
import * as Sentry from '@sentry/browser'
import { z } from 'zod'

import type { InferZustandParams } from 'utils/zustand'
import { CONFIG } from 'config'
import { OpenAPI } from 'openapi'

const ME_URL = new URL('/auth/me/', CONFIG.API_BASE_URL)
const GET_TOKEN_URL = new URL('/auth/api-token-auth/', CONFIG.API_BASE_URL)
const SIGNUP_URL = new URL('/auth/register/', CONFIG.API_BASE_URL)

const UserSchema = z.object({
  id: z.number(),
  email: z.string().email(),
  isStaff: z.boolean(),
  isSuperuser: z.boolean(),
})

type User = z.infer<typeof UserSchema>

export type RegistrationData = {
  email: string
  password: string
  passwordConfirmation: string
}

export const useAuthStore = create(
  devtools(
    immer(
      persist(
        (...args: any) => {
          const [set, get] = args as InferZustandParams<typeof useAuthStore>

          return {
            // as required to set type for variable in store
            authToken: undefined as string | undefined,
            status: 'blank' as 'blank' | 'authorizing' | 'unauthorized' | 'ok',
            user: undefined as User | undefined,

            initAuth: async () => {
              if (get().status !== 'blank') return
              get().loadUser()
            },

            loadUser: async () => {
              set({ status: 'authorizing' })

              const response = await fetch(ME_URL, { headers: { Authorization: `Bearer ${get().authToken}` } })
              if (!response.ok) {
                set({ status: 'unauthorized' })
                return
              }
              const userD = await response.json()
              const user = UserSchema.parse(userD)

              set({ status: 'ok', user })

              Sentry.setUser({ email: user.email, id: user.id.toString() })

              OpenAPI.TOKEN = get().authToken
              OpenAPI.WITH_CREDENTIALS = true
            },

            login: async (username: string, password: string) => {
              set({ status: 'authorizing' })

              const response = await fetch(GET_TOKEN_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ username, password }),
              })

              if (!response.ok) {
                set({ status: 'unauthorized' })
                return false
              }

              const { token } = await response.json()
              set({ authToken: token, status: 'ok' })
              get().loadUser()
              return true
            },

            signUp: async (
              data: RegistrationData,
            ): Promise<{ success: true; user: User } | { success: false; errors: Record<string, string> }> => {
              const response = await fetch(SIGNUP_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(data),
              })

              if (!response.ok) {
                const errors = await response.json()
                return { success: false, errors }
              }

              const responseData = await response.json()

              set({
                authToken: responseData.token,
                status: 'ok',
              })
              return { success: true, user: responseData as unknown as User }
            },
            logout: () => {
              set({ authToken: undefined, status: 'unauthorized', user: undefined })
            },
          }
        },
        {
          name: 'auth-store',

          // we should store only the token
          partialize: (state) => ({
            authToken: state.authToken,
          }),
        },
      ),
    ),
  ),
)

export const useUser = (): User => {
  const user = useAuthStore((state) => state.user)
  if (!user) throw new Error('User not loaded')
  return user
}
